import Node from '../interfaces/Node';
import MoveOptions from '../interfaces/MoveOptions';
import PositionDimention from '../interfaces/PositionDimention';
import FeTileLayoutEngineOptions from '../interfaces/FeTileLayoutEngineOptions';
import { Utils } from '../utils/utils';
import { CompactOptions } from '../types/CompactOptions';
import { ColumnOptions } from '../types/ColumnOptions';
import { SaveFunction } from '../types/SaveFunction';
import { OnChangeCallback } from '../types/OnChangeCallback';

/**
 * Defines the FeTileLayout engine that does most no DOM tile layout manipulation.
 * See FeTileLayout methods and vars for descriptions.
 *
 * NOTE: values should not be modified directly - call the main FeTileLayout API instead
 */
export default class FeTileLayoutEngine
{
  //#region Properties

  /** Number of columns in the layout */
  public column: number;

  /** Maximum number of rows in the layout */
  public maxRow: number;

  /** Array of nodes in the layout */
  public nodes: Node[];

  /** Array of nodes added to the layout */
  public addedNodes: Node[] = [];

  /** Array of nodes removed from the layout */
  public removedNodes: Node[] = [];

  /** Indicates if the engine is in batch mode */
  public batchMode: boolean;

  /** @internal callback to update the DOM attributes */
  protected onChange: OnChangeCallback;

  /** @internal Indicates if floating mode is enabled */
  protected _float: boolean;

  /** @internal Previous state of the floating mode */
  protected _prevFloat: boolean;

  /** @internal Cached layouts of different column counts for restoration */
  protected _layouts?: Node[][]; // maps column # to array of values nodes

  /** @internal Set during loading to add items after collision nodes */
  public _loading?: boolean;

  /** @internal Indicates if the engine is resizing items during column resize */
  protected _inColumnResize?: boolean;

  /** @internal Indicates if there are locked items in the layout */
  protected _hasLocked: boolean;

  /** @internal Unique global internal ID counter */
  public static _idSeq = 0;

  //#endregion Properties

  /**
   * Constructs a new FeTileLayoutEngine instance.
   * @param {FeTileLayoutEngineOptions} opts - Options for initializing the layout engine.
   */
  public constructor (opts: FeTileLayoutEngineOptions = {})
  {
    this.column = opts.column || 12;
    this.maxRow = opts.maxRow;
    this._float = opts.float;
    this.nodes = opts.nodes || [];
    this.onChange = opts.onChange;
  }

  /**
   * Performs a batch update on the layout.  If the flag is set to false, the batch update will be
   * performed.  If it is any other value, the batch update will prepare for a batch update.
   * @param {boolean} [flag=true] - If false, performs the batch update.  If any other value, prepares for a batch update.
   * @param {boolean} [doPack=true] - If true, packs nodes after batch update.
   * @returns {FeTileLayoutEngine} The current instance of the layout engine.
   */
  public batchUpdate (flag = true, doPack = true): FeTileLayoutEngine
  {
    if (!!this.batchMode === flag)
      return this;

    this.batchMode = flag;
    if (flag)
    {
      this._prevFloat = this._float;
      this._float = true; // let things go anywhere for now... will restore and possibly reposition later
      this.cleanNodes();
      this.saveInitial(); // since begin update (which is called multiple times) won't do this
    } else
    {
      this._float = this._prevFloat;
      delete this._prevFloat;
      if (doPack)
        this._packNodes();

      this._notify();
    }
    return this;
  }

  /**
   * Determines if the entire row area should be used for collision detection.
   * Use entire row for hitting area (will use bottom reverse sorted first) if we 
   * not actively moving DOWN and didn't already skip
   * @param {Node} node - The node to check.
   * @param {PositionDimention} nn - The new position of the node.
   * @returns {boolean} True if the entire row area should be used; otherwise, false.
   */
  protected _useEntireRowArea (node: Node, nn: PositionDimention): boolean
  {
    return (!this.float || this.batchMode && !this._prevFloat) && !this._hasLocked && (!node._moving || node._skipDown || nn.y <= node.y);
  }

  /**
   * @internal
   * Fixes collisions for a given node moving to a new location 'nn', with optional 'collide' node already found.
   * @param {Node} node - The node to move.
   * @param {PositionDimention} [nn=node] - The new position of the node.
   * @param {Node} [collide] - An optional node that is already found to be colliding.
   * @param {MoveOptions} [opt={}] - Optional move options.
   * @returns {boolean} True if the node was moved; otherwise, false.
   */
  protected _fixCollisions (node: Node, nn = node, collide?: Node, opt: MoveOptions = {}): boolean
  {
    this.sortNodes(-1); // from last to first, so recursive collision move items in the right order

    collide = collide || this.collide(node, nn); // REAL area collide for swap and skip if none...
    if (!collide)
      return false;

    // swap check: if we're actively moving in gravity mode, see if we collide with an object the same size
    if (node._moving && !opt.nested && !this.float)
    {
      if (this.swap(node, collide)) return true;
    }

    // during while() collisions MAKE SURE to check entire row so larger items don't leap frog small 
    // ones (push them all down starting last in layout)
    let area = nn;
    if (!this._loading && this._useEntireRowArea(node, nn))
    {
      area = { x: 0, w: this.column, y: nn.y, h: nn.h };
      collide = this.collide(node, area, opt.skip); // force new hit
    }

    let didMove = false;
    let newOpt: MoveOptions = { nested: true, pack: false };
    while (collide = collide || this.collide(node, area, opt.skip))
    {
      // could collide with more than 1 item... so repeat for each
      let moved: boolean;
      // if colliding with a locked item OR loading (move after) OR moving down with top gravity 
      // (and collide could move up) -> skip past the collide,
      // but remember that skip down so we only do this once (and push others otherwise).
      if (collide.locked || this._loading || node._moving && !node._skipDown && nn.y > node.y && !this.float
        // can take space we had, or before where we're going
        && (!this.collide(collide, { ...collide, y: node.y }, node) || !this.collide(collide, { ...collide, y: nn.y - collide.h }, node)))
      {
        node._skipDown = (node._skipDown || nn.y > node.y);
        moved = this.moveNode(node, { ...nn, y: collide.y + collide.h, ...newOpt });

        if ((collide.locked || this._loading) && moved)
        {
          Utils.copyPos(nn, node); // moving after lock become our new desired location
        }
        else if (!collide.locked && moved && opt.pack)
        {
          // we moved after and will pack: do it now and keep the original drop location, 
          // but past the old collide to see what else we might push way
          this._packNodes();
          nn.y = collide.y + collide.h;
          Utils.copyPos(node, nn);
        }

        didMove = didMove || moved;
      } else
      {
        // move collide down *after* where we will be, ignoring where we are now (don't collide with us)
        moved = this.moveNode(collide, { ...collide, y: nn.y + nn.h, skip: node, ...newOpt });
      }

      if (!moved)
      {
        return didMove;
      }

      // break if loop if we couldn't move after all (ex: maxRow, fixed)
      collide = undefined;
    }

    return didMove;
  }

  /**
   * Returns the nodes that intercept the given node. 
   * Optionally a different area can be used, as well as a second node to skip 
   * @param {Node} skip - The node to skip.
   * @param {PositionDimention} [area=skip] - The area to check for collisions.
   * @param {Node} [skip2] - An optional second node to skip.
   * @returns {Node | undefined} The node that intercepts the given node, or undefined if no collision is found.
   */
  public collide (skip: Node, area = skip, skip2?: Node): Node | undefined
  {
    const skipId = skip._id;
    const skip2Id = skip2?._id;
    return this.nodes.find(n => n._id !== skipId && n._id !== skip2Id && Utils.isIntercepted(n, area));
  }

  /**
   * Returns all nodes that intercept the given node.
   * @param {Node} skip - The node to skip.
   * @param {PositionDimention} [area=skip] - The area to check for collisions.
   * @param {Node} [skip2] - An optional second node to skip.
   * @returns {Node[]} An array of nodes that intercept the given node.
   */
  public collideAll (skip: Node, area = skip, skip2?: Node): Node[]
  {
    const skipId = skip._id;
    const skip2Id = skip2?._id;
    return this.nodes.filter(n => n._id !== skipId && n._id !== skip2Id && Utils.isIntercepted(n, area));
  }

  /**
   * Performs a pixel coverage collision based on where we started, returning the node that has the most coverage that is >50% mid line.
   * @param {Node} node - The node from which the collision detection starts.
   * @param {MoveOptions} o - The options for the move, including the rectangle to check against.
   * @param {Node[]} collides - The list of nodes to check for collisions.
   * @returns {Node | undefined} - The node that has the most coverage that is >50%, or undefined if no such node is found.
   */
  protected directionCollideCoverage (node: Node, o: MoveOptions, collides: Node[]): Node | undefined
  {
    if (!o.rect || !node._rect)
      return;

    let r0 = node._rect; // where started
    let r = { ...o.rect }; // where we are

    // update dragged rect to show where it's coming from (above or below, etc...)
    if (r.y > r0.y)
    {
      r.h += r.y - r0.y;
      r.y = r0.y;
    } else
    {
      r.h += r0.y - r.y;
    }

    if (r.x > r0.x)
    {
      r.w += r.x - r0.x;
      r.x = r0.x;
    } else
    {
      r.w += r0.x - r.x;
    }

    let collide: Node;
    let overMax = 0.5; // need >50%
    for (let n of collides)
    {
      if (n.locked || !n._rect)
      {
        break;
      }

      let r2 = n._rect; // overlapping target
      let yOver = Number.MAX_VALUE, xOver = Number.MAX_VALUE;
      // depending on which side we started from, compute the overlap % of coverage
      // (ex: from above/below we only compute the max horizontal line coverage)
      if (r0.y < r2.y)
      {
        // from above
        yOver = ((r.y + r.h) - r2.y) / r2.h;
      } else if (r0.y + r0.h > r2.y + r2.h)
      {
        // from below
        yOver = ((r2.y + r2.h) - r.y) / r2.h;
      }

      if (r0.x < r2.x)
      {
        // from the left
        xOver = ((r.x + r.w) - r2.x) / r2.w;
      } else if (r0.x + r0.w > r2.x + r2.w)
      {
        // from the right
        xOver = ((r2.x + r2.w) - r.x) / r2.w;
      }

      let over = Math.min(xOver, yOver);
      if (over > overMax)
      {
        overMax = over;
        collide = n;
      }
    }

    o.collide = collide; // save it so we don't have to find it again

    return collide;
  }

  /**
   * Caches the nodes' pixel rectangles used for collision detection during drag.
   * @param {number} w - The width of the layout.
   * @param {number} h - The height of the layout.
   * @param {number} top - The top offset.
   * @param {number} right - The right offset.
   * @param {number} bottom - The bottom offset.
   * @param {number} left - The left offset.
   * @returns {FeTileLayoutEngine} - The current instance of the layout engine.
   */
  public cacheRects (w: number, h: number, top: number, right: number, bottom: number, left: number): FeTileLayoutEngine
  {
    this.nodes.forEach(n =>
      n._rect = {
        y: n.y * h + top,
        x: n.x * w + left,
        w: n.w * w - left - right,
        h: n.h * h - top - bottom
      }
    );
    return this;
  }

  /**
   * Possibly swaps between 2 nodes (same size or column, not locked, touching), returning true if successful.
   * @param {Node} a - The first node to swap.
   * @param {Node} b - The second node to swap.
   * @returns {boolean | undefined} - True if the swap was successful, false otherwise.
   */
  public swap (a: Node, b: Node): boolean | undefined
  {
    if (!b || b.locked || !a || a.locked)
      return false;

    function _doSwap (): true
    {
      // assumes a is before b IFF they have different height (put after rather than exact swap)
      let x = b.x, y = b.y;
      b.x = a.x; b.y = a.y; // b -> a position
      if (a.h != b.h)
      {
        a.x = x; a.y = b.y + b.h; // a -> goes after b
      } else if (a.w != b.w)
      {
        a.x = b.x + b.w; a.y = y; // a -> goes after b
      } else
      {
        a.x = x; a.y = y; // a -> old b position
      }
      a._dirty = b._dirty = true;
      return true;
    }

    let touching: boolean; // remember if we called it (vs undefined)

    // same size and same row or column, and touching
    if (a.w === b.w && a.h === b.h && (a.x === b.x || a.y === b.y) && (touching = Utils.isTouching(a, b)))
    {
      return _doSwap();
    }

    // if ran test and fail, bail out
    if (touching === false)
      return;

    // check for taking same columns (but different height) and touching
    if (a.w === b.w && a.x === b.x && (touching || (touching = Utils.isTouching(a, b))))
    {
      if (b.y < a.y)
      {
        let t = a; a = b; b = t;
      }

      // swap a <-> b vars so a is first
      return _doSwap();
    }

    // if ran test and fail, bail out
    if (touching === false)
      return;

    // check if taking same row (but different width) and touching
    if (a.h === b.h && a.y === b.y && (touching || (touching = Utils.isTouching(a, b))))
    {
      if (b.x < a.x)
      {
        let t = a; a = b; b = t;
      }

      // swap a <-> b vars so a is first
      return _doSwap();
    }

    return false;
  }

  /**
   * Checks if a specified area is empty.
   * 
   * @param {number} x - The x-coordinate of the area.
   * @param {number} y - The y-coordinate of the area.
   * @param {number} w - The width of the area.
   * @param {number} h - The height of the area.
   * @returns {boolean} - Returns true if the area is empty, false otherwise.
   */
  public isAreaEmpty (x: number, y: number, w: number, h: number): boolean
  {
    let nn: Node = { x: x || 0, y: y || 0, w: w || 1, h: h || 1 };
    return !this.collide(nn);
  }

  /**
   * Re-layouts tile layout items to reclaim any empty space.
   * Optionally keeps the sort order exactly the same ('list' mode) vs truly finding empty spaces.
   * 
   * @param {CompactOptions} [layout='compact'] - The layout mode, either 'compact' or 'list'.
   * @param {boolean} [doSort=true] - Whether to sort the nodes before compacting.
   * @returns {FeTileLayoutEngine} - Returns the current instance of FeTileLayoutEngine.
   */
  public compact (layout: CompactOptions = 'compact', doSort = true): FeTileLayoutEngine
  {
    if (this.nodes.length === 0)
      return this;

    if (doSort)
      this.sortNodes();

    const wasBatch = this.batchMode;
    if (!wasBatch)
      this.batchUpdate();

    const wasColumnResize = this._inColumnResize;
    if (!wasColumnResize)
      this._inColumnResize = true; // faster addNode()

    let copyNodes = this.nodes;
    this.nodes = []; // pretend we have no nodes to conflict layout to start with...
    copyNodes.forEach((n, index, list) =>
    {
      let after: Node;
      if (!n.locked)
      {
        n.autoPosition = true;
        if (layout === 'list' && index)
          after = list[ index - 1 ];
      }
      this.addNode(n, false, after); // 'false' for add event trigger
    });

    if (!wasColumnResize)
      delete this._inColumnResize;

    if (!wasBatch)
      this.batchUpdate(false);

    return this;
  }

  /**
   * Enables or disables floating items.
   * 
   * @param {boolean} val - The value to set for floating items.
   */
  public set float (val: boolean)
  {
    if (this._float === val)
      return;

    this._float = val || false;
    if (!val)
    {
      this._packNodes()._notify();
    }
  }

  /**
   * Gets the current value of floating items.
   * 
   * @returns {boolean} - Returns true if floating items are enabled, false otherwise.
   */
  public get float (): boolean
  {
    return this._float || false;
  }

  /**
   * Sorts the nodes array from first to last, or reverse.
   * Called during collision/placement to force an order.
   * 
   * @param {1 | -1} [dir=1] - The direction to sort the nodes, 1 for ascending and -1 for descending.
   * @returns {FeTileLayoutEngine} - Returns the current instance of FeTileLayoutEngine.
   */
  public sortNodes (dir: 1 | -1 = 1): FeTileLayoutEngine
  {
    this.nodes = Utils.sort(this.nodes, dir);
    return this;
  }

  /**
   * @internal
   * Packs the nodes in the layout.
   * Called to top gravity pack the items back OR revert back to original Y positions when floating
   * @returns {FeTileLayoutEngine} - Returns the current instance of FeTileLayoutEngine.
   */
  protected _packNodes (): FeTileLayoutEngine
  {
    if (this.batchMode)
    {
      return this;
    }

    this.sortNodes(); // first to last

    if (this.float)
    {
      // restore original Y pos
      this.nodes.forEach(n =>
      {
        if (n._updating || n._originalPosition === undefined || n.y === n._originalPosition.y) return;
        let newY = n.y;
        while (newY > n._originalPosition.y)
        {
          --newY;
          let collide = this.collide(n, { x: n.x, y: newY, w: n.w, h: n.h });
          if (!collide)
          {
            n._dirty = true;
            n.y = newY;
          }
        }
      });
    } else
    {
      // top gravity pack
      this.nodes.forEach((n, i) =>
      {
        if (n.locked)
          return;

        while (n.y > 0)
        {
          let newY = i === 0 ? 0 : n.y - 1;
          let canBeMoved = i === 0 || !this.collide(n, { x: n.x, y: newY, w: n.w, h: n.h });
          if (!canBeMoved)
            break;

          // Note: must be dirty (from last position) for FeTileLayout::OnChange callback to update positions
          // and move items back. The user 'change' callback should detect changes from the original
          // starting position instead.
          n._dirty = (n.y !== newY);
          n.y = newY;
        }
      });
    }
    return this;
  }

  /**
   * Given a random node, makes sure it's coordinates/values are valid in the current layout
   * @param node The node to adjust
   * @param resizing If out of bound, resize down or move into the tile layout to fit ?
   */
  public prepareNode (node: Node, resizing?: boolean): Node
  {
    node._id = node._id ?? FeTileLayoutEngine._idSeq++;

    // if we're missing position, have the tile layout position us automatically (before we set them to 0,0)
    if (node.x === undefined || node.y === undefined || node.x === null || node.y === null)
    {
      node.autoPosition = true;
    }

    // assign defaults for missing required fields
    let defaults: Node = { x: 0, y: 0, w: 1, h: 1 };
    Utils.defaults(node, defaults);

    if (!node.autoPosition)
    {
      delete node.autoPosition;
    }

    if (!node.noResize)
    {
      delete node.noResize;
    }

    if (!node.noMove)
    {
      delete node.noMove;
    }

    Utils.sanitizeMinMax(node);

    // check for NaN (in case messed up strings were passed. can't do parseInt() || defaults.x above as 0 is valid #)
    if (typeof node.x == 'string')
    {
      node.x = Number(node.x);
    }

    if (typeof node.y == 'string')
    {
      node.y = Number(node.y);
    }

    if (typeof node.w == 'string')
    {
      node.w = Number(node.w);
    }

    if (typeof node.h == 'string')
    {
      node.h = Number(node.h);
    }

    if (isNaN(node.x))
    {
      node.x = defaults.x; node.autoPosition = true;
    }

    if (isNaN(node.y))
    {
      node.y = defaults.y; node.autoPosition = true;
    }

    if (isNaN(node.w))
    {
      node.w = defaults.w;
    }

    if (isNaN(node.h))
    {
      node.h = defaults.h;
    }

    this.nodeBoundFix(node, resizing);
    return node;
  }

  /**
   * Part2 of preparing a node to fit inside our tile layout - checks for x,y,w from tile layout dimensions.
   * Given a random node, makes sure it's coordinates/values are valid in the current layout 
   * @param node The node to adjust
   * @param resizing If out of bound, resize down or move into the tile layout to fit ?
   * @returns {FeTileLayoutEngine} - Returns the current instance of FeTileLayoutEngine.
   */
  public nodeBoundFix (node: Node, resizing?: boolean): FeTileLayoutEngine
  {
    let before = node._originalPosition || Utils.copyPos({}, node);

    if (node.maxW)
    {
      node.w = Math.min(node.w, node.maxW);
    }

    if (node.maxH)
    {
      node.h = Math.min(node.h, node.maxH);
    }

    if (node.minW && node.minW <= this.column)
    {
      node.w = Math.max(node.w, node.minW);
    }

    if (node.minH)
    {
      node.h = Math.max(node.h, node.minH);
    }

    // if user loaded a larger than allowed item for current # of columns,
    // remember it's position & width so we can restore back (1 -> 12 column) #1655 #1985
    // IFF we're not in the middle of column resizing!
    const saveOrig = (node.x || 0) + (node.w || 1) > this.column;
    if (saveOrig && this.column < 12 && !this._inColumnResize && node._id && this.findCacheLayout(node, 12) === -1)
    {
      let copy = { ...node }; // need _id + positions
      if (copy.autoPosition || copy.x === undefined) 
      {
        delete copy.x; delete copy.y;
      }
      else 
      {
        copy.x = Math.min(11, copy.x);
      }
      copy.w = Math.min(12, copy.w || 1);
      this.cacheOneLayout(copy, 12);
    }

    if (node.w > this.column)
    {
      node.w = this.column;
    } else if (node.w < 1)
    {
      node.w = 1;
    }

    if (this.maxRow && node.h > this.maxRow)
    {
      node.h = this.maxRow;
    } else if (node.h < 1)
    {
      node.h = 1;
    }

    if (node.x < 0)
    {
      node.x = 0;
    }

    if (node.y < 0)
    {
      node.y = 0;
    }

    if (node.x + node.w > this.column)
    {
      if (resizing)
      {
        node.w = this.column - node.x;
      } else
      {
        node.x = this.column - node.w;
      }
    }

    if (this.maxRow && node.y + node.h > this.maxRow)
    {
      if (resizing)
      {
        node.h = this.maxRow - node.y;
      } else
      {
        node.y = this.maxRow - node.h;
      }
    }

    if (!Utils.samePos(node, before))
    {
      node._dirty = true;
    }

    return this;
  }

  /**
   * Returns a list of modified nodes from their original values
   * @param verify If true, only return nodes that have actually changed position/size
   * @returns {Node[]} - An array of modified nodes.
   */
  public getDirtyNodes (verify?: boolean): Node[]
  {
    // compare original x,y,w,h instead as _dirty can be a temporary state
    if (verify)
    {
      return this.nodes.filter(n => n._dirty && !Utils.samePos(n, n._originalPosition));
    }

    return this.nodes.filter(n => n._dirty);
  }

  /** 
   * @internal 
   * Call this to call onChange callback with dirty nodes so DOM can be updated 
   * @param removedNodes The nodes that were removed.
   * @returns {FeTileLayoutEngine} - Returns the current instance of FeTileLayoutEngine.
   */
  protected _notify (removedNodes?: Node[]): FeTileLayoutEngine
  {
    if (this.batchMode || !this.onChange)
      return this;

    let dirtyNodes = (removedNodes || []).concat(this.getDirtyNodes());
    this.onChange(dirtyNodes);
    return this;
  }

  /**
   * @internal
   * Removes dirty and last tried information from the nodes.
   * @returns {FeTileLayoutEngine} - Returns the current instance of FeTileLayoutEngine.
   */
  public cleanNodes (): FeTileLayoutEngine
  {
    if (this.batchMode)
      return this;

    this.nodes.forEach(n =>
    {
      delete n._dirty;
      delete n._lastTried;
    });
    return this;
  }

  /** 
   * @internal 
   * Called to save initial position/size to track real dirty state.
   * Note: should be called right after we call change event (so next API is can detect changes)
   * as well as right before we start move/resize/enter (so we can restore items to prev values) 
   * @returns {FeTileLayoutEngine} - Returns the current instance of FeTileLayoutEngine.
   */
  public saveInitial (): FeTileLayoutEngine
  {
    this.nodes.forEach(n =>
    {
      n._originalPosition = Utils.copyPos({}, n);
      delete n._dirty;
    });
    this._hasLocked = this.nodes.some(n => n.locked);
    return this;
  }

  /** 
   * @internal 
   * Restore all the nodes back to initial values (called when we leave) 
   * @returns {FeTileLayoutEngine} - Returns the current instance of FeTileLayoutEngine.
   */
  public restoreInitial (): FeTileLayoutEngine
  {
    this.nodes.forEach(n =>
    {
      if (Utils.samePos(n, n._originalPosition))
        return;

      Utils.copyPos(n, n._originalPosition);
      n._dirty = true;
    });
    this._notify();
    return this;
  }

  /** 
   * Finds the first available empty spot for the given node width/height, updating the x,y attributes. return true if found.
   * optionally you can pass your own existing node list and column count, otherwise defaults to that engine data.
   * Optionally pass a item to start search AFTER, meaning the order will remain the same but possibly have empty slots we skipped
   * @param node The node to find an empty position for.
   * @param nodeList The list of nodes to search for an empty position.
   * @param column The column count to use for the search.
   * @param after The node to start the search after.
   * @returns {boolean} - Returns true if an empty position was found; otherwise, false.
   */
  public findEmptyPosition (node: Node, nodeList = this.nodes, column = this.column, after?: Node): boolean
  {
    let start = after ? after.y * column + (after.x + after.w) : 0;
    let found = false;
    for (let i = start; !found; ++i)
    {
      let x = i % column;
      let y = Math.floor(i / column);
      if (x + node.w > column)
      {
        continue;
      }

      let box = { x, y, w: node.w, h: node.h };
      if (!nodeList.find(n => Utils.isIntercepted(box, n)))
      {
        if (node.x !== x || node.y !== y) node._dirty = true;
        node.x = x;
        node.y = y;
        delete node.autoPosition;
        found = true;
      }
    }

    return found;
  }

  /** 
   * Call to add the given node to our list, fixing collision and re-packing 
   * @param node The node to add.
   * @param triggerAddEvent If true, the add event will be triggered.
   * @param after The node to add after.
   * @returns {Node} - Returns the node that was added.
   */
  public addNode (node: Node, triggerAddEvent = false, after?: Node): Node
  {
    let dup = this.nodes.find(n => n._id === node._id);
    if (dup)
      return dup; // prevent inserting twice! return it instead.

    // skip prepareNode if we're in middle of column resize (not new) but do check for bounds!
    this._inColumnResize ? this.nodeBoundFix(node) : this.prepareNode(node);
    delete node._temporaryRemoved;
    delete node._removeDOM;

    let skipCollision: boolean;
    if (node.autoPosition && this.findEmptyPosition(node, this.nodes, this.column, after))
    {
      delete node.autoPosition; // found our slot
      skipCollision = true;
    }

    this.nodes.push(node);
    if (triggerAddEvent)
    {
      this.addedNodes.push(node);
    }

    if (!skipCollision)
      this._fixCollisions(node);

    if (!this.batchMode)
    {
      this._packNodes()._notify();
    }

    return node;
  }

  /** 
   * Call to remove the given node from our list, fixing collision and re-packing 
   * @param node The node to remove.
   * @param removeDOM If true, the DOM element will be removed.
   * @param triggerEvent If true, the remove event will be triggered.
   * @returns {FeTileLayoutEngine} - Returns the current instance of FeTileLayoutEngine.
   */
  public removeNode (node: Node, removeDOM = true, triggerEvent = false): FeTileLayoutEngine
  {
    if (!this.nodes.find(n => n._id === node._id))
    {
      // console.log(`Error: FeTileLayoutEngine.removeNode() node._id=${node._id} not found!`)
      return this;
    }

    if (triggerEvent)
    {
      // we wait until final drop to manually track removed items (rather than during drag)
      this.removedNodes.push(node);
    }

    if (removeDOM)
      node._removeDOM = true; // let callback remove actual HTML (used to set _id to null, but then we loose layout info)

    // don't use 'faster' .splice(findIndex(),1) in case node isn't in our list, or in multiple times.
    this.nodes = this.nodes.filter(n => n._id !== node._id);
    if (!node._isAboutToRemove)
      this._packNodes(); // if dragged out, no need to relayout as already done...

    this._notify([ node ]);
    return this;
  }

  /** 
   * Call to remove all nodes from our list, fixing collision and re-packing 
   * @param removeDOM If true, the DOM element will be removed.
   * @param triggerEvent If true, the remove event will be triggered.
   * @returns {FeTileLayoutEngine} - Returns the current instance of FeTileLayoutEngine.
   */
  public removeAll (removeDOM = true, triggerEvent = true): FeTileLayoutEngine
  {
    delete this._layouts;
    if (!this.nodes.length)
      return this;

    // let callback remove actual HTML (used to set _id to null, but then we loose layout info)
    removeDOM && this.nodes.forEach(n => n._removeDOM = true);
    const removedNodes = this.nodes;
    this.removedNodes = triggerEvent ? removedNodes : [];
    this.nodes = [];
    return this._notify(removedNodes);
  }

  /** 
   * Checks if item can be moved (layout constrain) vs moveNode(), returning true if was able to move.
   * In more complicated cases (maxRow) it will attempt at moving the item and fixing
   * others in a clone first, then apply those changes if still within specs. 
   * @param node The node to move.
   * @param o The options for the move.
   * @returns {boolean} - Returns true if the item was able to move; otherwise, false.
   */
  public moveNodeCheck (node: Node, o: MoveOptions): boolean
  {
    if (!this.changedPositionConstrain(node, o))
      return false;

    o.pack = true;

    // simpler case: move item directly...
    if (!this.maxRow)
    {
      return this.moveNode(node, o);
    }

    // complex case: create a clone with NO maxRow (will check for out of bounds at the end)
    let clonedNode: Node;
    let clone = new FeTileLayoutEngine({
      column: this.column,
      float: this.float,
      nodes: this.nodes.map(n =>
      {
        if (n._id === node._id)
        {
          clonedNode = { ...n };
          return clonedNode;
        }
        return { ...n };
      })
    });

    if (!clonedNode)
      return false;

    // check if we're covering 50% collision and could move, while still being under maxRow or at least not making it worse
    // (case where item was somehow added past our max #2449)
    let canMove = clone.moveNode(clonedNode, o) && clone.getRow() <= Math.max(this.getRow(), this.maxRow);
    // else check if we can force a swap (float=true, or different shapes) on non-resize
    if (!canMove && !o.resizing && o.collide)
    {
      let collide = o.collide.el.tileLayoutNode; // find the source node the clone collided with at 50%
      if (this.swap(node, collide))
      { // swaps and mark dirty
        this._notify();
        return true;
      }
    }

    if (!canMove)
      return false;

    // if clone was able to move, copy those mods over to us now instead of caller trying to do this all over!
    // Note: we can't use the list directly as elements and other parts point to actual node, so copy content
    clone.nodes.filter(n => n._dirty).forEach(c =>
    {
      let n = this.nodes.find(a => a._id === c._id);
      if (!n)
        return;

      Utils.copyPos(n, c);
      n._dirty = true;
    });

    this._notify();
    return true;
  }

  /** 
   * Returns true if can fit in tile layout height constrain only (always true if no maxRow) 
   * @param node The node to check.
   * @returns {boolean} - Returns true if the node can fit; otherwise, false.
   */
  public willItFit (node: Node): boolean
  {
    delete node._willFitPosition;
    if (!this.maxRow)
      return true;

    // create a clone with NO maxRow and check if still within size
    let clone = new FeTileLayoutEngine({
      column: this.column,
      float: this.float,
      nodes: this.nodes.map(n => { return { ...n }; })
    });
    let n = { ...node }; // clone node so we don't mod any settings on it but have full autoPosition and min/max as well! #1687
    this.cleanupNode(n);
    delete n.el;
    delete n._id;
    //delete n.content; 
    delete n.tileLayout;
    clone.addNode(n);
    if (clone.getRow() <= this.maxRow)
    {
      node._willFitPosition = Utils.copyPos({}, n);
      return true;
    }
    return false;
  }

  /** 
   * True if x,y or w,h are different after clamping to min/max 
   * @param node The node to check.
   * @param p The position to check.
   * @returns {boolean} - Returns true if the position was changed; otherwise, false.
   */
  public changedPositionConstrain (node: Node, p: PositionDimention): boolean
  {
    // first make sure w,h are set for caller
    p.w = p.w || node.w;
    p.h = p.h || node.h;
    if (node.x !== p.x || node.y !== p.y)
      return true;

    // check constrained w,h
    if (node.maxW)
    {
      p.w = Math.min(p.w, node.maxW);
    }

    if (node.maxH)
    {
      p.h = Math.min(p.h, node.maxH);
    }

    if (node.minW)
    {
      p.w = Math.max(p.w, node.minW);
    }

    if (node.minH)
    {
      p.h = Math.max(p.h, node.minH);
    }

    return (node.w !== p.w || node.h !== p.h);
  }

  /** 
   * Returns true if the passed in node was actually moved (checks for no-op and locked) 
   * @param node The node to move.
   * @param o The options for the move.
   * @returns {boolean} - Returns true if the node was moved; otherwise, false.
   */
  public moveNode (node: Node, o: MoveOptions): boolean
  {
    if (!node || !o)
      return false;

    let wasUndefinedPack: boolean;
    if (o.pack === undefined && !this.batchMode)
    {
      wasUndefinedPack = o.pack = true;
    }

    // constrain the passed in values and check if we're still changing our node
    if (typeof o.x !== 'number')
    {
      o.x = node.x;
    }

    if (typeof o.y !== 'number')
    {
      o.y = node.y;
    }

    if (typeof o.w !== 'number')
    {
      o.w = node.w;
    }

    if (typeof o.h !== 'number')
    {
      o.h = node.h;
    }

    let resizing = (node.w !== o.w || node.h !== o.h);
    let nn: Node = Utils.copyPos({}, node, true); // get min/max out first, then opt positions next
    Utils.copyPos(nn, o);
    this.nodeBoundFix(nn, resizing);
    Utils.copyPos(o, nn);

    if (!o.forceCollide && Utils.samePos(node, o))
      return false;

    let prevPos: PositionDimention = Utils.copyPos({}, node);

    // check if we will need to fix collision at our new location
    let collides = this.collideAll(node, nn, o.skip);
    let needToMove = true;
    if (collides.length)
    {
      let activeDrag = node._moving && !o.nested;
      // check to make sure we actually collided over 50% surface area while dragging
      let collide = activeDrag ? this.directionCollideCoverage(node, o, collides) : collides[ 0 ];
      // if we're enabling creation of sub-layouts on the fly, see if we're covering 80% of either one, if we didn't already do that
      if (activeDrag && collide && node.tileLayout?.opts?.subLayoutDynamic && !node.tileLayout._isTemp)
      {
        let over = Utils.areaIntercept(o.rect, collide._rect);
        let a1 = Utils.area(o.rect);
        let a2 = Utils.area(collide._rect);
        let perc = over / (a1 < a2 ? a1 : a2);
        if (perc > .8)
        {
          collide.tileLayout.makeSubLayout(collide.el, undefined, node);
          collide = undefined;
        }
      }

      if (collide)
      {
        needToMove = !this._fixCollisions(node, nn, collide, o); // check if already moved...
      } else
      {
        needToMove = false; // we didn't cover >50% for a move, skip...
        if (wasUndefinedPack)
          delete o.pack;
      }
    }

    // now move (to the original ask vs the collision version which might differ) and repack things
    if (needToMove)
    {
      node._dirty = true;
      Utils.copyPos(node, nn);
    }

    if (o.pack)
    {
      this._packNodes()._notify();
    }

    return !Utils.samePos(node, prevPos); // pack might have moved things back
  }

  /** 
   * Returns true if the passed in node was actually resized (checks for no-op and locked) 
   * @param node The node to resize.
   * @param o The options for the resize.
   * @returns {boolean} - Returns true if the node was resized; otherwise, false.
   */
  public getRow (): number
  {
    return this.nodes.reduce((row, n) => Math.max(row, n.y + n.h), 0);
  }

  /** 
   * Returns true if the passed in node was actually resized (checks for no-op and locked) 
   * @param node The node to resize.
   * @param o The options for the resize.
   * @returns {boolean} - Returns true if the node was resized; otherwise, false.
   */
  public beginUpdate (node: Node): FeTileLayoutEngine
  {
    if (!node._updating)
    {
      node._updating = true;
      delete node._skipDown;
      if (!this.batchMode)
        this.saveInitial();
    }
    return this;
  }

  /** 
   * Returns true if the passed in node was actually resized (checks for no-op and locked) 
   * @param node The node to resize.
   * @param o The options for the resize.
   * @returns {boolean} - Returns true if the node was resized; otherwise, false.
   */
  public endUpdate (): FeTileLayoutEngine
  {
    let n = this.nodes.find(n => n._updating);
    if (n)
    {
      delete n._updating;
      delete n._skipDown;
    }
    return this;
  }

  /** 
   * Saves a copy of the largest column layout (eg 12 even when rendering oneColumnMode) so 
   * we don't loose orig layout, returning a list of items for serialization 
   * @param saveElement If true, the element will be saved.
   * @param saveCallback The callback to save the element.
   * @returns {Node[]} - An array of items for serialization.
   */
  public save (saveElement = true, saveCallback?: SaveFunction): Node[]
  {
    // use the highest layout for any saved info so we can have full detail on reload #1849
    let len = this._layouts?.length;
    let layout = len && this.column !== (len - 1) ? this._layouts[ len - 1 ] : null;
    let list: Node[] = [];
    this.sortNodes();
    this.nodes.forEach(n =>
    {
      let wl = layout?.find(l => l._id === n._id);
      // use layout info fields instead if set
      let w: Node = { ...n, ...(wl || {}) };
      Utils.removeInternalForSave(w, !saveElement);
      if (saveCallback)
        saveCallback(n, w);

      list.push(w);
    });
    return list;
  }

  /** 
   * @internal 
   * Called whenever a node is added or moved - updates the cached layouts 
   * @param nodes The nodes to cache.
   * @param column The column to cache.
   * @returns {FeTileLayoutEngine} - Returns the current instance of FeTileLayoutEngine.
   */
  public layoutsNodesChange (nodes: Node[]): FeTileLayoutEngine
  {
    if (!this._layouts || this._inColumnResize)
      return this;

    // remove smaller layouts - we will re-generate those on the fly... larger ones need to update
    this._layouts.forEach((layout, column) =>
    {
      if (!layout || column === this.column)
        return this;

      if (column < this.column)
      {
        this._layouts[ column ] = undefined;
      }
      else
      {
        // we save the original x,y,w (h isn't cached) to see what actually changed to propagate better.
        // NOTE: we don't need to check against out of bound scaling/moving as that will be 
        // done when using those cache values.
        let ratio = column / this.column;
        nodes.forEach(node =>
        {
          if (!node._originalPosition)
            return; // didn't change (newly added ?)

          let n = layout.find(l => l._id === node._id);
          if (!n)
            return; // no cache for new nodes. Will use those values.

          // Y changed, push down same amount
          // TODO: detect doing item 'swaps' will help instead of move (especially in 1 column mode)
          if (n.y >= 0 && node.y !== node._originalPosition.y)
          {
            n.y += (node.y - node._originalPosition.y);
          }

          // X changed, scale from new position
          if (node.x !== node._originalPosition.x)
          {
            n.x = Math.round(node.x * ratio);
          }

          // width changed, scale from new width
          if (node.w !== node._originalPosition.w)
          {
            n.w = Math.round(node.w * ratio);
          }

          // ...height always carries over from cache
        });
      }
    });
    return this;
  }

  /**
   * @internal 
   * Called to scale the item width & position up/down based on the column change.
   * Note we store previous layouts (especially original ones) to make it possible to go
   * from say 12 -> 1 -> 12 and get back to where we were.
   * Note: items will never be outside of the current column boundaries. default (moveScale). Ignored for 1 column
   * @param prevColumn previous number of columns
   * @param column new column number
   * @param layout specify the type of re-layout that will happen (position, size, etc...).
   * @returns {FeTileLayoutEngine} - Returns the current instance of FeTileLayoutEngine.
   */
  public columnChanged (prevColumn: number, column: number, layout: ColumnOptions = 'moveScale'): FeTileLayoutEngine
  {
    if (!this.nodes.length || !column || prevColumn === column)
      return this;

    // in this mode no layout is done whatsoever, up to the caller to handle it
    if (layout === 'none')
      return this;

    // simpler shortcuts layouts
    const doCompact = layout === 'compact' || layout === 'list';
    if (doCompact)
    {
      this.sortNodes(1); // sort with original layout once and only once (new column will affect order otherwise)
    }

    // cache the current layout in case they want to go back (like 12 -> 1 -> 12) as it requires original data IFF we're sizing down (see below)
    if (column < prevColumn)
      this.cacheLayout(this.nodes, prevColumn);

    // do this EARLY as it will call saveInitial() so we can detect where we started for _dirty and collision
    this.batchUpdate();

    let newNodes: Node[] = [];
    // current column reverse sorting so we can insert last to front (limit collision)
    let nodes = doCompact ? this.nodes : Utils.sort(this.nodes, -1);

    // see if we have cached previous layout IFF we are going up in size (restore) otherwise always
    // generate next size down from where we are (looks more natural as you gradually size down).
    if (column > prevColumn && this._layouts)
    {
      const cacheNodes = this._layouts[ column ] || [];
      // ...if not, start with the largest layout (if not already there) as down-scaling is more accurate
      // by pretending we came from that larger column by assigning those values as starting point
      let lastIndex = this._layouts.length - 1;
      if (!cacheNodes.length && prevColumn !== lastIndex && this._layouts[ lastIndex ]?.length)
      {
        prevColumn = lastIndex;
        this._layouts[ lastIndex ].forEach(cacheNode =>
        {
          let n = nodes.find(n => n._id === cacheNode._id);
          if (n)
          {
            // still current, use cache info positions
            if (!doCompact && !cacheNode.autoPosition)
            {
              n.x = cacheNode.x ?? n.x;
              n.y = cacheNode.y ?? n.y;
            }
            n.w = cacheNode.w ?? n.w;
            if (cacheNode.x == undefined || cacheNode.y === undefined)
              n.autoPosition = true;
          }
        });
      }

      // if we found cache re-use those nodes that are still current
      cacheNodes.forEach(cacheNode =>
      {
        let j = nodes.findIndex(n => n._id === cacheNode._id);
        if (j !== -1)
        {
          const n = nodes[ j ];
          // still current, use cache info positions
          if (doCompact)
          {
            n.w = cacheNode.w; // only w is used, and don't trim the list
            return;
          }

          if (cacheNode.autoPosition || isNaN(cacheNode.x) || isNaN(cacheNode.y))
          {
            this.findEmptyPosition(cacheNode, newNodes);
          }

          if (!cacheNode.autoPosition)
          {
            n.x = cacheNode.x ?? n.x;
            n.y = cacheNode.y ?? n.y;
            n.w = cacheNode.w ?? n.w;
            newNodes.push(n);
          }
          nodes.splice(j, 1);
        }
      });
    }

    // much simpler layout that just compacts
    if (doCompact)
    {
      this.compact(layout, false);
    } else
    {
      // ...and add any extra non-cached ones
      if (nodes.length)
      {
        if (typeof layout === 'function')
        {
          layout(column, prevColumn, newNodes, nodes);
        } else
        {
          let ratio = doCompact ? 1 : column / prevColumn;
          let move = (layout === 'move' || layout === 'moveScale');
          let scale = (layout === 'scale' || layout === 'moveScale');
          nodes.forEach(node =>
          {
            // NOTE: x + w could be outside of the layout, but addNode() below will handle that
            node.x = (column === 1 ? 0 : (move ? Math.round(node.x * ratio) : Math.min(node.x, column - 1)));
            node.w = ((column === 1 || prevColumn === 1) ? 1 : scale ? (Math.round(node.w * ratio) || 1) : (Math.min(node.w, column)));
            newNodes.push(node);
          });
          nodes = [];
        }
      }

      // finally re-layout them in reverse order (to get correct placement)
      newNodes = Utils.sort(newNodes, -1);
      this._inColumnResize = true; // prevent cache update
      this.nodes = []; // pretend we have no nodes to start with (add() will use same structures) to simplify layout
      newNodes.forEach(node =>
      {
        this.addNode(node, false); // 'false' for add event trigger
        delete node._originalPosition; // make sure the commit doesn't try to restore things back to original
      });
    }

    this.nodes.forEach(n => delete n._originalPosition); // clear _orig before batch=false so it doesn't handle float=true restore
    this.batchUpdate(false, !doCompact);
    delete this._inColumnResize;
    return this;
  }

  /**
   * Called to cache the given layout internally to the given location so we can restore back when column changes size
   * @param nodes list of nodes
   * @param column corresponding column index to save it under
   * @param clear if true, will force other caches to be removed (default false)
   * @returns {FeTileLayoutEngine} - Returns the current instance of FeTileLayoutEngine.
   */
  public cacheLayout (nodes: Node[], column: number, clear = false): FeTileLayoutEngine
  {
    let copy: Node[] = [];
    nodes.forEach((n, i) =>
    {
      // make sure we have an id in case this is new layout, else re-use id already set
      if (n._id === undefined)
      {
        const existing = n.id ? this.nodes.find(n2 => n2.id === n.id) : undefined; // find existing node using users id
        n._id = existing?._id ?? FeTileLayoutEngine._idSeq++;
      }
      copy[ i ] = { x: n.x, y: n.y, w: n.w, _id: n._id }; // only thing we change is x,y,w and id to find it back
    });
    this._layouts = clear ? [] : this._layouts || []; // use array to find larger quick
    this._layouts[ column ] = copy;
    return this;
  }

  /**
   * Called to cache the given node layout internally to the given location so we can restore back when column changes size
   * @param node single node to cache
   * @param column corresponding column index to save it under
   * @returns {FeTileLayoutEngine} - Returns the current instance of FeTileLayoutEngine.
   */
  public cacheOneLayout (n: Node, column: number): FeTileLayoutEngine
  {
    n._id = n._id ?? FeTileLayoutEngine._idSeq++;
    let l: Node = { x: n.x, y: n.y, w: n.w, _id: n._id };
    if (n.autoPosition || n.x === undefined)
    {
      delete l.x; delete l.y;
      if (n.autoPosition)
        l.autoPosition = true;
    }
    this._layouts = this._layouts || [];
    this._layouts[ column ] = this._layouts[ column ] || [];
    let index = this.findCacheLayout(n, column);
    if (index === -1)
      this._layouts[ column ].push(l);
    else
      this._layouts[ column ][ index ] = l;

    return this;
  }

  /**
   * Called to find the index of the given node in the cache layout
   * @param n node to find
   * @param column column to search
   * @returns {number} - Returns the index of the node in the cache layout.
   */
  protected findCacheLayout (n: Node, column: number): number | undefined
  {
    return this._layouts?.[ column ]?.findIndex(l => l._id === n._id) ?? -1;
  }

  /**
   * Called to remove the given node from the cache layout
   * @param n node to remove
   * @param column column to remove from
   * @returns {FeTileLayoutEngine} - Returns the current instance of FeTileLayoutEngine.
   */
  public removeNodeFromLayoutCache (n: Node)
  {
    if (!this._layouts)
    {
      return;
    }

    for (let i = 0; i < this._layouts.length; i++)
    {
      let index = this.findCacheLayout(n, i);
      if (index !== -1)
      {
        this._layouts[ i ].splice(index, 1);
      }
    }
  }

  /** 
   * Called to remove all internal values but the _id 
   * @param node The node to cleanup.
   * @returns {FeTileLayoutEngine} - Returns the current instance of FeTileLayoutEngine.
   */
  public cleanupNode (node: Node): FeTileLayoutEngine
  {
    for (let prop in node)
    {
      if (prop[ 0 ] === '_' && prop !== '_id')
        delete node[ prop ];
    }
    return this;
  }
}
